/**
 * «Sentiment di oggi» (SPEC-026 §8.4): le due letture affiancate — Borsa e
 * Crypto — con gli indicatori misurati, le ragioni con la fonte e, in fondo,
 * il confronto: di pari passo o contrapposti.
 *
 * Non è un segnale operativo, e la card lo dice: è contesto. Ogni ambito ha
 * la sua lettura, il suo pulsante «Aggiorna» e la sua ora automatica.
 *
 * ## La lettura precedente resta (F-178)
 *
 * Con le letture previste alle 13 e alle 21, chi apriva la pagina alle 10
 * vedeva «Nessuna lettura oggi» in entrambe le colonne. Ora l'esito mostrato è
 * l'ultima lettura **completata**, di qualunque giorno, finché la nuova non la
 * sostituisce — con la sua età bene in vista e un'etichetta quando non è di
 * oggi, perché un sentiment di ieri sera letto come di stamattina è peggio di
 * nessun sentiment. Una lettura in corso o fallita oggi non la nasconde.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ASSET_DOMAIN_LABELS, type AssetDomain } from '@/lib/asset-domain';
import { formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Card, Icon, Switch } from '@/ui';
import { useRunSentiment, useSentimentToday, useUpdateSentimentSettings, type SentimentReading, type SentimentSettings } from './sentiment-api';
import { DIRECTION_LABEL, DIRECTION_TONE, DIVERGENCE_LABEL, formatAge, isStale, signedScore } from './sentiment-format';

function DomainColumn({
  domain,
  current,
  shown,
  todayDate,
  settings,
}: {
  domain: AssetDomain;
  /** La riga di oggi, in qualunque stato. */
  current: SentimentReading | null;
  /** L'esito da mostrare: l'ultima lettura completata, di oggi o precedente. */
  shown: SentimentReading | null;
  todayDate: string;
  settings: SentimentSettings;
}) {
  const run = useRunSentiment();
  const save = useUpdateSentimentSettings();
  const [time, setTime] = useState(settings.runAtLocalTime);
  const busy = current?.status === 'queued' || current?.status === 'running';
  const previous = shown !== null && shown.readingDate !== todayDate;
  const stale = shown?.completedAt ? isStale(shown.completedAt) : false;
  // Gli indicatori sono misurati prima del modello: se oggi la lettura è
  // fallita senza un esito precedente da mostrare, i numeri di oggi valgono.
  const measured = shown ?? current;

  return (
    <div className="sentiment__column" data-domain={domain}>
      <div className="sentiment__column-head">
        <strong>{ASSET_DOMAIN_LABELS[domain]}</strong>
        <Button variant="secondary" onClick={() => run.mutate(domain)} disabled={run.isPending || busy}>
          {busy ? 'Lettura in corso…' : shown || current ? 'Aggiorna' : 'Leggi adesso'}
        </Button>
      </div>

      {run.isError && (
        <Alert variant="error" title="Lettura non avviata">
          {run.error instanceof Error ? run.error.message : 'Errore imprevisto.'}
        </Alert>
      )}

      {!shown && !current && (
        <p className="cell--minor">
          Nessuna lettura ancora.
          {settings.enabled && ` La prima è prevista alle ${settings.runAtLocalTime}.`}
        </p>
      )}

      {shown?.completedAt && (
        <p className="sentiment__age" data-stale={stale || undefined}>
          {previous && <Badge tone={stale ? 'warning' : 'neutral'}>{stale ? 'lettura datata' : 'lettura precedente'}</Badge>}{' '}
          <span title={formatDateTime(shown.completedAt)}>
            {previous ? `del ${formatDate(shown.readingDate)}, ` : 'letta '}
            {formatAge(shown.completedAt)}
          </span>
          {previous && !current && settings.enabled && (
            <span className="cell--minor"> · la prossima alle {settings.runAtLocalTime}</span>
          )}
        </p>
      )}

      {current?.status === 'failed' && (
        <Alert variant="warning" title="Lettura di oggi fallita">
          {current.errorMessage ?? 'Motivo non registrato.'}
          {shown && ' Resta visibile l’ultima lettura riuscita.'}
        </Alert>
      )}

      {shown?.direction && (
        <>
          <div className="sentiment__verdict">
            <Badge tone={DIRECTION_TONE[shown.direction]}>{DIRECTION_LABEL[shown.direction]}</Badge>
            <span className="sentiment__score">{signedScore(shown.score)}</span>
            <span className="cell--minor">confidenza {shown.confidence}</span>
          </div>
          {shown.summary && <p className="sentiment__summary">{shown.summary}</p>}
        </>
      )}
      {measured && measured.indicators.length > 0 && (
        <ul className="sentiment__indicators" aria-label={`Indicatori ${ASSET_DOMAIN_LABELS[domain]}`}>
          {measured.indicators.map((indicator) => (
            <li key={indicator.name} title={`${indicator.source}${indicator.detail ? ` · ${indicator.detail}` : ''}`}>
              <span className="cell--minor">{indicator.name}</span> <strong>{indicator.value}</strong>
            </li>
          ))}
        </ul>
      )}
      {measured && measured.notMeasured.length > 0 && (
        <p className="cell--minor">Non misurati: {measured.notMeasured.join(', ')}.</p>
      )}
      {shown && shown.drivers.length > 0 && (
        <ul className="sentiment__drivers">
          {shown.drivers.slice(0, 4).map((driver, index) => (
            <li key={index}>
              {driver.text}
              {driver.sourceUrl && (
                <>
                  {' '}
                  <a href={driver.sourceUrl} target="_blank" rel="noopener noreferrer">
                    fonte
                  </a>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {shown?.completedAt && (
        <p className="cell--minor">
          Letta {formatDateTime(shown.completedAt)} · rev. {shown.revision}
        </p>
      )}

      <div className="sentiment__settings">
        <Switch
          label="Lettura automatica"
          checked={settings.enabled}
          onChange={(enabled) => save.mutate({ domain, enabled })}
          disabled={save.isPending}
        />
        <label className="sentiment__time">
          <span className="cell--minor">alle</span>
          <input
            type="time"
            value={time}
            aria-label={`Ora della lettura ${ASSET_DOMAIN_LABELS[domain]}`}
            onChange={(event) => setTime(event.target.value)}
            onBlur={() => time !== settings.runAtLocalTime && save.mutate({ domain, runAtLocalTime: time })}
          />
          <span className="cell--minor">({settings.timezone})</span>
        </label>
      </div>
    </div>
  );
}

export function MarketSentimentCard() {
  const today = useSentimentToday();
  const data = today.data;
  // Un errore o un dato di forma inattesa: la card non compare. È un
  // arricchimento, e non deve poter rompere la pagina del briefing.
  if (today.isError || !data || !data.readings || !data.settings) return null;

  const shownFor = (domain: AssetDomain): SentimentReading | null => {
    const latest = data.latest?.[domain] ?? null;
    if (latest) return latest;
    const current = data.readings[domain];
    return current?.status === 'completed' ? current : null;
  };

  return (
    <Card
      title="Sentiment di mercato"
      subtitle="Dove punta il mercato di ciascun ambito, misurato prima del briefing. È contesto, non un segnale operativo."
      action={
        <Link className="btn btn--ghost btn--sm" to="/briefing/sentiment">
          <Icon name="history" /> Storico
        </Link>
      }
    >
      <div className="sentiment">
        {(['stock', 'crypto'] as const).map((domain) => (
          <DomainColumn
            key={domain}
            domain={domain}
            current={data.readings[domain]}
            shown={shownFor(domain)}
            todayDate={data.readingDate}
            settings={data.settings[domain]}
          />
        ))}
      </div>
      <p className="sentiment__comparison" data-divergence={data.comparison.divergence}>
        <Badge tone={data.comparison.divergence === 'opposed' ? 'warning' : data.comparison.divergence === 'aligned' ? 'positive' : 'neutral'}>
          {DIVERGENCE_LABEL[data.comparison.divergence]}
        </Badge>{' '}
        {data.comparison.note}
      </p>
    </Card>
  );
}
